<template>
  <p class="placeholder-glow" v-if="isLoading">
    <span class="placeholder col-12 skeleton-nav"></span>
  </p>
  <div v-html="headerData.header_content" v-else dir="ltr"></div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, onUnmounted } from "vue";
import LanguageSwitcher from "@/components/common/language-switcher.vue";
import { useRoute } from "vue-router";
import LayoutService from "@/services/layout";
import handleError from "@/helper/handleError";

export default defineComponent({
  name: "MainHeader",
  components: {
    LanguageSwitcher,
  },
  setup() {
    const route = useRoute();
    // Prefer the URL over localStorage, and resolve it reactively:
    //  - localStorage holds the LAST product opened, not the one in ?productID=,
    //    so a stale id rendered another product's header on this page.
    //  - vue-router's initial navigation is async, so route.query is still empty
    //    while setup() runs; reading it once left this undefined on a direct link.
    const productID = computed(
      () => (route.query.productID as string) || localStorage.getItem("productID") || ""
    );
    const headerData = ref({
      header_content: String,
      noscript_tags: [],
      script_tags: [],
      style_tags: [],
    });
    const isLoading = ref(true);
    
    // Keep track of added elements for cleanup
    const addedElements = ref<HTMLElement[]>([]);

    // ===================== Methods ===============
    const appendTagsToHead = () => {
      // Clean up previously added elements
      cleanupHeadElements();
      
      const head = document.head || document.getElementsByTagName('head')[0];
      
      // Append noscript tags
      if (headerData.value.noscript_tags && headerData.value.noscript_tags.length > 0) {
        headerData.value.noscript_tags.forEach((noscriptHtml: string) => {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = noscriptHtml.trim();
          const noscriptElement = tempDiv.firstChild as HTMLElement;
          
          if (noscriptElement) {
            // Add a data attribute to track our added elements
            noscriptElement.setAttribute('data-dynamic-header', 'true');
            head.appendChild(noscriptElement);
            addedElements.value.push(noscriptElement);
          }
        });
      }
      
      // Append script tags
      if (headerData.value.script_tags && headerData.value.script_tags.length > 0) {
        headerData.value.script_tags.forEach((scriptHtml: string) => {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = scriptHtml.trim();
          const scriptElement = tempDiv.firstChild as HTMLScriptElement;
          
          if (scriptElement) {
            // For script tags, we need to create a new script element and copy attributes/content
            const newScript = document.createElement('script');
            
            // Copy attributes
            Array.from(scriptElement.attributes).forEach(attr => {
              newScript.setAttribute(attr.name, attr.value);
            });
            
            // Add tracking attribute
            newScript.setAttribute('data-dynamic-header', 'true');
            
            // Copy content
            if (scriptElement.innerHTML) {
              newScript.innerHTML = scriptElement.innerHTML;
            }
            
            head.appendChild(newScript);
            addedElements.value.push(newScript);
          }
        });
      }
      
      // Append style tags (join all together and insert directly)
      if (headerData.value.style_tags && headerData.value.style_tags.length > 0) {
        const allStyles = headerData.value.style_tags.join('\n');
        head.insertAdjacentHTML('beforeend', allStyles);
      }
    };

    const cleanupHeadElements = () => {
      // Remove previously added elements
      addedElements.value.forEach(element => {
        if (element.parentNode) {
          element.parentNode.removeChild(element);
        }
      });
      addedElements.value = [];
    };

    const getHeader = () => {
      isLoading.value = true;
      if (!productID.value) return;
      
      LayoutService.getHeader(productID.value)
        .then((response) => {
          isLoading.value = false;
          headerData.value = response.data.data;
          
          // Append tags to head after data is loaded
          appendTagsToHead();
        })
        .catch((err: any) => {
          isLoading.value = false;
          handleError(err);
        });
    };

    // ================== watch ====================
    watch(productID, getHeader, { immediate: true });

    // ==================== onUnmounted ===============
    onUnmounted(() => {
      // Clean up when component is destroyed
      cleanupHeadElements();
    });

    return {
      getHeader,
      headerData,
      productID,
      isLoading
    };
  },
});
</script>

<style lang="scss">
.top_nav {
  direction: ltr !important;
}
.skeleton-nav {
  min-height: 2.5em;
}
</style>